'use es6';

import KeyMirror from 'react-utils/keyMirror';
import { Set as ImmutableSet } from 'immutable';
import { CHART_PURPLE } from 'HubStyleTokens/colors';
export const BASE_URL = 'ads/v1';
export const MAX_SIMULTANEOUS_ACCOUNTS = 3;
export const QueryParams = {
  ADWORDS_CONNECT_SUCCESS: 'adwordsConnectedSuccess',
  FACEBOOK_CONNECT_SUCCESS: 'facebookConnectSuccess'
};
export const ALL_NETWORKS = 'ALL';
export const WARNING_THRESHOLD = 0.8;
export const Networks = KeyMirror({
  ADWORDS: null,
  FACEBOOK: null,
  LINKEDIN: null
});
export const NetworksNonCap = {
  ADWORDS: 'Google',
  FACEBOOK: 'Facebook',
  LINKEDIN: 'LinkedIn'
};
export const NetworksV2 = {
  FACEBOOK: 'Meta',
  ADWORDS: 'Google',
  LINKEDIN: 'LinkedIn'
};
export const NETWORKS_WITH_ALL = [...Object.keys(Networks), ALL_NETWORKS];
export const EntityTypes = {
  CAMPAIGN: 'campaigns',
  ADGROUP: 'adgroup',
  AD: 'ad',
  KEYWORD: 'keyword',
  NEGATIVE_KEYWORD: 'negative-keyword',
  SEQUENCE: 'ad-sequence',
  AD_SEQ_CAMPAIGNS_ATTRACT_STAGE: 'ad_seq_campaigns_attract_stage',
  AD_SEQ_CAMPAIGNS_CONVERT_STAGE: 'ad_seq_campaigns_convert_stage',
  AD_SEQ_CAMPAIGNS_CLOSE_STAGE: 'ad_seq_campaigns_close_stage'
};
export const EntityTypeValues = Object.values(EntityTypes);
export const EntityChildren = {
  [EntityTypes.SEQUENCE]: [EntityTypes.CAMPAIGN],
  [EntityTypes.CAMPAIGN]: [EntityTypes.ADGROUP],
  [EntityTypes.ADGROUP]: [EntityTypes.AD, EntityTypes.KEYWORD, EntityTypes.NEGATIVE_KEYWORD]
};
export const EntityTypesByNetwork = {
  [Networks.ADWORDS]: [EntityTypes.CAMPAIGN, EntityTypes.ADGROUP, EntityTypes.AD, EntityTypes.KEYWORD, EntityTypes.NEGATIVE_KEYWORD],
  [Networks.FACEBOOK]: [EntityTypes.CAMPAIGN, EntityTypes.ADGROUP, EntityTypes.AD],
  [Networks.LINKEDIN]: [EntityTypes.CAMPAIGN, EntityTypes.ADGROUP, EntityTypes.AD]
};
export const CampaignDetailsTabTypes = KeyMirror({
  MANAGE: null,
  OPTIMIZE: null,
  ANALYZE: null
});
export const AttributionViews = KeyMirror({
  FIRST_WEB_SESSION: null,
  PRE_CONTACT_INFLUENCED: null,
  FIRST_FORM_SUBMISSION: null,
  ALL_FORM_SUBMISSIONS: null,
  REENGAGEMENT: null
});
export const AdWordsGroupings = {
  DEFAULT: 'keywords',
  KEYWORDS: 'keywords',
  ADS: 'ads'
};
export const AdWordsNetworks = KeyMirror({
  targetGoogleSearch: null,
  targetSearchNetwork: null,
  targetContentNetwork: null
});
export const AdWordsAdTypes = KeyMirror({
  TEXT_AD: null,
  TEMPLATE_AD: null,
  LEGACY_APP_INSTALL_AD: null,
  EXPANDED_TEXT_AD: null,
  RESPONSIVE_SEARCH_AD: null,
  SMART_CAMPAIGN_AD: null,
  RESPONSIVE_DISPLAY_AD: null,
  IMAGE_AD: null
});
export const AdWordsCampaignTypes = KeyMirror({
  SEARCH: null,
  MOBILE: null,
  SHOPPING: null,
  VIDEO: null,
  SMART: null
});
export const LocationExtensionMatchPreference = {
  MATCH_ALL_LOCATIONS: 'MATCH_ALL_LOCATIONS',
  MATCH_FILTERS: 'MATCH_FILTERS',
  MATCH_LOCATIONS: 'MATCH_LOCATIONS'
};
export const LocationExtensionDevicePreference = {
  ALL: 'ALL',
  DESKTOP: 'DESKTOP',
  MOBILE: 'MOBILE'
}; // https://git.hubteam.com/HubSpot/Ads/blob/master/AdsBase/src/main/java/com/hubspot/ads/data/adwords/typewrappers/ChannelType.java

export const AdWordsChannelType = KeyMirror({
  SEARCH: null,
  DISPLAY: null,
  SHOPPING: null,
  UNKNOWN: null,
  MULTI_CHANNEL: null,
  // Video is not in this channelType enum b/c for some reason adwords doesnts upport
  // it in their XML api but it can be returned by the backend
  Video: null
});
export const AdWordsChannelSubType = KeyMirror({
  SEARCH_MOBILE_APP: null,
  // this is the mobile app campaign we need (app installs)
  DISPLAY_MOBILE_APP: null,
  SEARCH_EXPRESS: null,
  DISPLAY_EXPRESS: null,
  UNIVERSAL_APP_CAMPAIGN: null
});
export const AdWordsCreationNavigationSteps = KeyMirror({
  LANDING_PAGE: null,
  NAME: null,
  AD: null,
  KEYWORDS: null,
  GEOGRAPHY: null,
  BUDGET: null,
  REVIEW: null
});
export const StatusMenuCodes = KeyMirror({
  ACTIVE: null,
  ENABLED: null,
  PAUSED: null,
  DISABLED: null
});
export const StatusMenuNetworkMappings = {
  ADWORDS: {
    ENABLED: StatusMenuCodes.ACTIVE,
    PAUSED: StatusMenuCodes.PAUSED,
    UNKNOWN: StatusMenuCodes.DISABLED,
    REMOVED: StatusMenuCodes.DISABLED,
    DISABLED: StatusMenuCodes.DISABLED
  },
  FACEBOOK: {
    ENABLED: StatusMenuCodes.ACTIVE,
    ACTIVE: StatusMenuCodes.ACTIVE,
    PAUSED: StatusMenuCodes.PAUSED,
    DELETED: StatusMenuCodes.DISABLED,
    ARCHIVED: StatusMenuCodes.DISABLED
  },
  LINKEDIN: {
    ACTIVE: StatusMenuCodes.ACTIVE,
    ENABLED: StatusMenuCodes.ACTIVE,
    PAUSED: StatusMenuCodes.PAUSED,
    UNKNOWN: StatusMenuCodes.DISABLED,
    REMOVED: StatusMenuCodes.DISABLED,
    DISABLED: StatusMenuCodes.DISABLED,
    DISAPPROVED: StatusMenuCodes.DISABLED,
    PENDING_REVIEW: StatusMenuCodes.DISABLED,
    DRAFT: StatusMenuCodes.DISABLED,
    COMPLETED: StatusMenuCodes.DISABLED
  }
}; // Has associated I18n keys

export const PauseableTypes = KeyMirror({
  campaign: null,
  adgroup: null,
  keyword: null,
  ad: null,
  creative: null,
  adSet: null
});
export const StatusMap = KeyMirror({
  ACTIVE: null,
  ENABLED: null,
  PAUSED: null,
  UNKNOWN: null,
  REMOVED: null,
  DISABLED: null,
  DELETED: null,
  ARCHIVED: null
});
export const AdWordsStatusMap = KeyMirror({
  ENABLED: null,
  PAUSED: null,
  UNKNOWN: null,
  REMOVED: null,
  DISABLED: null
});
export const DisabledNetworkMappings = {
  ADWORDS: [StatusMap.UNKNOWN, StatusMap.REMOVED, StatusMap.DISABLED],
  FACEBOOK: [StatusMap.DELETED, StatusMap.ARCHIVED],
  LINKEDIN: [StatusMap.UNKNOWN, StatusMap.REMOVED, StatusMap.DISABLED]
}; // returns 3 types of status codes, ENABLED, PAUSED, REMOVED that correspond to the UserFacingEntityStatus.java enum
// for use when grabbing contacts for a campaign

export const getContactsStatus = status => {
  switch (status) {
    case 'ENABLED':
    case 'ACTIVE':
    case 'COMPLETED':
    case 'PENDING_REVIEW':
      return 'ENABLED';

    case 'DRAFT':
    case 'PAUSED':
      return 'PAUSED';

    default:
      return 'REMOVED';
  }
};
export const FBCampaignObjective = KeyMirror({
  BRAND_AWARENESS: null,
  CANVAS_APP_ENGAGEMENT: null,
  CANVAS_APP_INSTALLS: null,
  CONVERSIONS: null,
  EVENT_RESPONSES: null,
  EXTERNAL: null,
  LEAD_GENERATION: null,
  LINK_CLICKS: null,
  LOCAL_AWARENESS: null,
  MOBILE_APP_ENGAGEMENT: null,
  MOBILE_APP_INSTALLS: null,
  OFFER_CLAIMS: null,
  PAGE_LIKES: null,
  POST_ENGAGEMENT: null,
  PRODUCT_CATALOG_SALES: null,
  REACH: null,
  VIDEO_VIEWS: null,
  NONE: null
}); // website visit and traffic are the same concept, but traffic will be deprecated

export const LinkedInObjective = KeyMirror({
  CREATIVE_ENGAGEMENT: null,
  LEAD_GENERATION: null,
  VIDEO_VIEW: null,
  WEBSITE_TRAFFIC: null,
  WEBSITE_VISIT: null
}); // Mappings for generic entity status across all networks,
// useful for network-agnostic status filtering.
// See UserFacingEntityStatus.java

export const EntityStatusMap = KeyMirror({
  ENABLED: null,
  PAUSED: null,
  REMOVED: null
});
export const ALL_STATUSES = ImmutableSet([EntityStatusMap.ENABLED, EntityStatusMap.PAUSED, EntityStatusMap.REMOVED]);
export const EntityStatus = KeyMirror({
  ACTIVE: null,
  ADGROUP_PAUSED: null,
  CAMPAIGN_PAUSED: null,
  COMPLETED: null,
  DELETED: null,
  DRAFT: null,
  NOT_APPROVED: null,
  PAUSED: null,
  PENDING_REVIEW: null,
  SCHEDULED: null
});
export const EntityStatusToStatusTagMap = {
  [EntityStatus.ACTIVE]: {
    use: 'active',
    hollow: false
  },
  [EntityStatus.ADGROUP_PAUSED]: {
    use: 'paused',
    hollow: false
  },
  [EntityStatus.CAMPAIGN_PAUSED]: {
    use: 'paused',
    hollow: false
  },
  [EntityStatus.COMPLETED]: {
    use: 'default',
    hollow: true
  },
  [EntityStatus.DELETED]: {
    use: 'default',
    hollow: true
  },
  [EntityStatus.DRAFT]: {
    use: 'draft',
    hollow: false
  },
  [EntityStatus.NOT_APPROVED]: {
    use: 'danger',
    hollow: false
  },
  [EntityStatus.PAUSED]: {
    use: 'paused',
    hollow: false
  },
  [EntityStatus.PENDING_REVIEW]: {
    color: CHART_PURPLE,
    hollow: true
  },
  [EntityStatus.SCHEDULED]: {
    use: 'warning',
    hollow: true
  }
};
export const FBAdFormat = KeyMirror({
  RIGHT_COLUMN_STANDARD: null,
  DESKTOP_FEED_STANDARD: null,
  MOBILE_FEED_STANDARD: null,
  MOBILE_FEED_BASIC: null,
  MOBILE_INTERSTITIAL: null,
  MOBILE_BANNER: null,
  MOBILE_MEDIUM_RECTANGLE: null,
  MOBILE_NATIVE: null,
  INSTAGRAM_STANDARD: null,
  AUDIENCE_NETWORK_OUTSTREAM_VIDEO: null,
  INSTANT_ARTICLE_STANDARD: null,
  INSTREAM_VIDEO_DESKTOP: null,
  INSTREAM_VIDEO_MOBILE: null,
  NULL_FORMAT: null // Custom value provided by backend. Reserved for situations where preview not available.

});
export const FunnelStages = {
  IMPRESSIONS: 'impressions',
  CLICKS: 'clicks',
  CONVERSIONS: 'conversions',
  QUALIFIEDLEADS: 'qualifiedLeads',
  SALESQUALIFIEDLEADS: 'salesQualifiedLeads',
  OPPORTUNITIES: 'opportunities',
  CUSTOMERS: 'customers',
  DEALS: 'deals',
  LEADS: 'leads'
};
export const ContactListFunnelStage = KeyMirror({
  CONVERSION: null,
  CUSTOMER: null,
  MARKETING_QUALIFIED_LEAD: null,
  SALES_QUALIFIED_LEAD: null,
  OPPORTUNITY: null,
  LEAD: null
});
export const SortDirections = {
  ASCENDING: 1,
  DESCENDING: -1
};
export const OnboardingWizardNavigationSteps = KeyMirror({
  SELECT_ACCOUNTS: null,
  CONNECT_FACEBOOK: null,
  SELECT_FACEBOOK_AD_ACCOUNT: null,
  // Single FB accounts (deprecated)
  SELECT_FACEBOOK_AD_ACCOUNTS: null,
  // Multiple FB accounts
  CONNECT_ADWORDS: null,
  SELECT_ADWORDS_CUSTOMER_ACCOUNT: null,
  SELECT_ADWORDS_MANAGED_ACCOUNT: null,
  CONNECT_LINKEDIN: null,
  SELECT_LINKEDIN_AD_ACCOUNTS: null,
  LEAD_SYNCING: null,
  PIXEL_TRACKING: null,
  ROI_CALCULATION: null
});
export const AdWordsKeywordTypes = KeyMirror({
  BROAD: 'broad',
  EXACT: 'exact',
  PHRASE: 'phrase'
});
export const AdWordsKeywordServingStatusMap = KeyMirror({
  ELIGIBLE: null,
  RARELY_SERVED: null,
  UNKNOWN: null
});
export const NotificationTypes = KeyMirror({
  INFO: null,
  SUCCESS: null,
  ERROR: null,
  WARNING: null,
  DANGER: null
}); // https://developers.google.com/adwords/api/docs/reference/v201509/AdGroupService.AdGroup
// https://developers.google.com/adwords/api/docs/appendix/limits

export const AdWordsCharacterLimits = {
  ad: {
    headlinePart1: 30,
    headlinePart2: 30,
    path1: 15,
    path2: 15,
    description: 90,
    headline: 25,
    description1: 90,
    description2: 90,
    displayUrl: 35,
    displayUrlVisible: 35,
    campaign: 128 // 128 bytes...

  },
  adExtensions: {
    calloutText: 25,
    // is key just `text`?
    linkText: 25,
    // is key just `text`?
    line2: 35,
    // description line 1
    line3: 35,
    // description line 2
    reviewSourceAndText: 67
  },
  keyword: 80,
  // and 10 words
  adGroup: 255,
  campaignName: 128
};
export const ColumnValues = KeyMirror({
  AVERAGE_POSITION: null,
  CLICK_THROUGH_RATE: null,
  CONVERSION_RATE: null,
  CONVERSION_PRICE: null,
  MAX_CPC: null,
  TOP_CPC: null,
  FIRST_PAGE_CPC: null,
  CPC: null,
  IMPRESSIONS: null,
  CLICKS: null,
  CONVERSIONS: null,
  CUSTOMERS: null,
  ROI: null,
  DEALS: null,
  DEALS_REVENUE: null,
  ENGAGEMENTS: null,
  LIKES: null,
  COST: null,
  QUALITY: null,
  AD_ACCOUNT: null,
  TYPE: null,
  NAME: null,
  LEADS: null,
  MARKETING_QUALIFIED_LEADS: null,
  SALES_QUALIFIED_LEADS: null,
  OPPORTUNITIES: null,
  KEY_RESULTS: null,
  SESSIONS: null,
  CONVERTED_DEALS_REVENUE: null,
  CONVERTED_COST: null,
  CONVERTED_CPC: null,
  COST_PER_DEAL: null,
  COST_PER_LEAD: null,
  COST_PER_MQL: null,
  COST_PER_OPPORTUNITY: null,
  COST_PER_SESSION: null,
  COST_PER_SQL: null,
  COST_PER_CUSTOMER: null,
  P25_VIDEO_VIEWS: null,
  WORKFLOWS: null
});
export const TextColumns = [ColumnValues.AD_ACCOUNT, ColumnValues.TYPE];
export const AdsRoiReportingColumns = [ColumnValues.CUSTOMERS, ColumnValues.LEADS, ColumnValues.MARKETING_QUALIFIED_LEADS, ColumnValues.SALES_QUALIFIED_LEADS, ColumnValues.OPPORTUNITIES, ColumnValues.DEALS_REVENUE, ColumnValues.ROI, ColumnValues.COST_PER_DEAL, ColumnValues.COST_PER_LEAD, ColumnValues.COST_PER_MQL, ColumnValues.COST_PER_OPPORTUNITY, ColumnValues.COST_PER_SESSION, ColumnValues.COST_PER_SQL];
export const ColumnValuesToKey = {
  [ColumnValues.NAME]: ['entity', 'name'],
  [ColumnValues.AD_ACCOUNT]: ['entity', 'accountId'],
  [ColumnValues.TYPE]: ['entity', 'type'],
  [ColumnValues.AVERAGE_POSITION]: ['entity', 'averagePosition'],
  [ColumnValues.CLICK_THROUGH_RATE]: ['report', 'clickThroughRate'],
  [ColumnValues.CLICKS]: ['report', 'clicks'],
  [ColumnValues.COST]: ['report', 'cost'],
  [ColumnValues.CPC]: ['report', 'costPerClick'],
  [ColumnValues.CONVERSION_RATE]: ['entity', 'conversionRate'],
  [ColumnValues.MAX_CPC]: ['entity', 'maxCpc'],
  [ColumnValues.TOP_CPC]: ['entity', 'topOfPageCpc'],
  [ColumnValues.FIRST_PAGE_CPC]: ['entity', 'firstPageCpc'],
  [ColumnValues.CONVERSION_PRICE]: ['report', 'costPerNewContact'],
  [ColumnValues.CUSTOMERS]: ['report', 'customers'],
  [ColumnValues.DEALS]: ['report', 'deals'],
  [ColumnValues.IMPRESSIONS]: ['report', 'impressions'],
  [ColumnValues.CONVERSIONS]: ['report', 'newContacts'],
  [ColumnValues.DEALS_REVENUE]: ['report', 'revenue'],
  [ColumnValues.ROI]: ['report', 'roi'],
  [ColumnValues.QUALITY]: ['entity', 'qualityScore'],
  [ColumnValues.LEADS]: ['report', 'leads'],
  [ColumnValues.MARKETING_QUALIFIED_LEADS]: ['report', 'marketingQualifiedLeads'],
  [ColumnValues.SALES_QUALIFIED_LEADS]: ['report', 'salesQualifiedLeads'],
  [ColumnValues.OPPORTUNITIES]: ['report', 'opportunities'],
  [ColumnValues.ENGAGEMENTS]: ['report', 'engagements'],
  [ColumnValues.LIKES]: ['report', 'likes'],
  [ColumnValues.KEY_RESULTS]: ['report', 'keyResults'],
  [ColumnValues.SESSIONS]: ['report', 'sessions'],
  [ColumnValues.CONVERTED_COST]: ['report', 'convertedCost'],
  [ColumnValues.CONVERTED_DEALS_REVENUE]: ['report', 'convertedRevenue'],
  [ColumnValues.CONVERTED_CPC]: ['report', 'costPerClick'],
  [ColumnValues.COST_PER_DEAL]: ['report', 'costPerDeal'],
  [ColumnValues.COST_PER_LEAD]: ['report', 'costPerLead'],
  [ColumnValues.COST_PER_MQL]: ['report', 'costPerMql'],
  [ColumnValues.COST_PER_OPPORTUNITY]: ['report', 'costPerOpportunity'],
  [ColumnValues.COST_PER_SESSION]: ['report', 'costPerSession'],
  [ColumnValues.COST_PER_SQL]: ['report', 'costPerSql'],
  [ColumnValues.COST_PER_CUSTOMER]: ['report', 'costPerCustomer'],
  [ColumnValues.P25_VIDEO_VIEWS]: ['report', 'p25VideoViews']
};
export const ColumnValuesToContactParams = {
  [ColumnValues.CONVERSIONS]: ContactListFunnelStage.CONVERSION,
  [ColumnValues.CUSTOMERS]: ContactListFunnelStage.CUSTOMER,
  [ColumnValues.LEADS]: ContactListFunnelStage.LEAD,
  [ColumnValues.MARKETING_QUALIFIED_LEADS]: ContactListFunnelStage.MARKETING_QUALIFIED_LEAD,
  [ColumnValues.SALES_QUALIFIED_LEADS]: ContactListFunnelStage.SALES_QUALIFIED_LEAD,
  [ColumnValues.OPPORTUNITIES]: ContactListFunnelStage.OPPORTUNITY
};
export const LeadAdEditSteps = {
  LEAD_AD: 'leadAd',
  FOLLOW_UP: 'followUp',
  AUTOMATE: 'automate'
};
export const LeadAdEditStepsByNetwork = {
  [Networks.FACEBOOK]: [LeadAdEditSteps.LEAD_AD, LeadAdEditSteps.AUTOMATE],
  [Networks.LINKEDIN]: [LeadAdEditSteps.AUTOMATE]
};
export const AdExtensionTypes = KeyMirror({
  CALLOUT: null,
  SITELINK: null
});
export const AppStoreEnum = {
  APPLE_ITUNES: 1,
  GOOGLE_PLAY: 2
};
export const AppStore = KeyMirror({
  APPLE_ITUNES: null,
  GOOGLE_PLAY: null,
  UNKNOWN: null
});
export const AdWordsCampaignDevicePreferences = KeyMirror({
  DESKTOP: null,
  MOBILE: null,
  TABLET: null,
  CONNECTED_TV: null
});
export const DAYS_PER_WEEK = 7;
export const DaysOfWeek = KeyMirror({
  SUNDAY: null,
  MONDAY: null,
  TUESDAY: null,
  WEDNESDAY: null,
  THURSDAY: null,
  FRIDAY: null,
  SATURDAY: null
});
export const QuarterHourStrings = ['00:00', '00:15', '00:30', '00:45', '01:00', '01:15', '01:30', '01:45', '02:00', '02:15', '02:30', '02:45', '03:00', '03:15', '03:30', '03:45', '04:00', '04:15', '04:30', '04:45', '05:00', '05:15', '05:30', '05:45', '06:00', '06:15', '06:30', '06:45', '07:00', '07:15', '07:30', '07:45', '08:00', '08:15', '08:30', '08:45', '09:00', '09:15', '09:30', '09:45', '10:00', '10:15', '10:30', '10:45', '11:00', '11:15', '11:30', '11:45', '12:00', '12:15', '12:30', '12:45', '13:00', '13:15', '13:30', '13:45', '14:00', '14:15', '14:30', '14:45', '15:00', '15:15', '15:30', '15:45', '16:00', '16:15', '16:30', '16:45', '17:00', '17:15', '17:30', '17:45', '20:00', '20:15', '20:30', '20:45', '21:00', '21:15', '21:30', '21:45', '22:00', '22:15', '22:30', '22:45', '23:00', '23:15', '23:30', '23:45'];
export const AdWordsAdExtensionRadioOptions = KeyMirror({
  ALL: null,
  CAMPAIGN: null,
  ADGROUP: null
});
export const DeliveryMethod = KeyMirror({
  STANDARD: null,
  ACCELERATED: null,
  UNKNOWN: null
});
export const ErrorTypesToUIAlertType = {
  INFO: 'info',
  WARNING: 'warning',
  DANGER: 'danger',
  ERROR: 'danger',
  SUCCESS: 'success',
  TIP: 'tip'
};
export const BudgetUnits = KeyMirror({
  PERCENTAGE: null,
  MONEY: null
});
export const AdWordsRecommendedBudgetValues = {
  CLICKS_PER_DAY_BASE: 5,
  MINIMUM_DAILY_BUDGET: 25,
  MAX_CLICKS_MIN_THRESHOLD: 1
};
export const BiddingStrategyType = KeyMirror({
  BUDGET_OPTIMIZER: null,
  CONVERSION_OPTIMIZER: null,
  MANUAL_CPC: null,
  MANUAL_CPM: null,
  PAGE_ONE_PROMOTED: null,
  TARGET_SPEND: null,
  ENHANCED_CPC: null,
  TARGET_CPA: null,
  TARGET_ROAS: null,
  TARGET_OUTRANK_SHARE: null,
  MAXIMIZE_CONVERSIONS: null,
  NONE: null
});
export const BiddingStrategySource = KeyMirror({
  ADGROUP: null,
  CRITERION: null,
  CAMPAIGN: null
});
export const TOP_LEVEL_BIDDING_STRATEGIES = KeyMirror({
  CPC: null,
  SHARED: null,
  TARGET_CPA: null,
  TARGET_ROAS: null,
  TARGET_SPEND: null,
  MAXIMIZE_CONVERSIONS: null
});
export const CPC_BIDDING_STRATEGIES = KeyMirror({
  MANUAL_CPC: null,
  ENHANCED_CPC: null,
  TARGET_SPEND: null
});
export const AdWordsTargetRoasRange = {
  MIN: 1,
  MAX: 10000
};
export const AdWordsCountingType = KeyMirror({
  ONE_PER_CLICK: null,
  MANY_PER_CLICK: null
});
export const AdWordsConversionTrackerCategory = KeyMirror({
  DEFAULT: null,
  PAGE_VIEW: null,
  PURCHASE: null,
  SIGNUP: null,
  LEAD: null,
  DOWNLOAD: null
});
export const AdWordsTextFormat = KeyMirror({
  ONE_LINE: null,
  TWO_LINE: null,
  HIDDEN: null
});
export const AdWordsTrackingCodeType = KeyMirror({
  WEBPAGE: null,
  // See note: https://developers.google.com/adwords/api/docs/guides/conversion-tracking#conversions_on_websites
  WEBPAGE_ONCLICK: null,
  CLICK_TO_CALL: null
});
export const AdWordsAppConversionType = KeyMirror({
  NONE: null,
  DOWNLOAD: null,
  IN_APP_PURCHASE: null,
  FIRST_OPEN: null
});
export const AdWordsAppPlatform = KeyMirror({
  APPLE_ITUNES: null,
  GOOGLE_PLAY: null,
  MOBILE_APP_CHANNEL: null,
  NONE: null
}); // TODO: Not sure if these keys are right

export const AdWordsConversionTrackerType = KeyMirror({
  AdWordsConversionTracker: null,
  UploadConversion: null,
  AdCallMetricsConversion: null,
  AppConversion: null,
  WebsiteCallMetricsConversion: null
});
export const AdWordsConversionTrackerStatus = KeyMirror({
  ENABLED: null,
  DISABLED: null,
  HIDDEN: null
});
export const AdWordsConversionTrackerValueOption = KeyMirror({
  SAME_VALUE: null,
  VALUE_VARIES: null,
  DONT_ASSIGN: null
}); // See: https://developers.google.com/adwords/api/docs/reference/v201605/CampaignCriterionService.CampaignCriterion#bidmodifier

export const AdWordsCampaignBiddingModifierLimits = {
  location: {
    lowerLimit: -90,
    upperLimit: 900
  }
};
export const RoiTypes = KeyMirror({
  DEALS_ROI: null,
  LEAD_COST_ROI: null
});
export const AdSpendCurrencyChoices = {
  PORTAL: 'PORTAL',
  NETWORK: 'NETWORK'
};
export const FBUnsupportedCurrencies = KeyMirror({
  CLF: null
});
export const HtmlEscapeCodes = {
  DASH: '\u2014'
};
export const ZeroStateData = `${HtmlEscapeCodes.DASH}`;
export const AudienceTypes = KeyMirror({
  CONTACT_LIST: null,
  WEBSITE: null,
  LOOKALIKE: null,
  SAVED: null,
  COMPANY_LIST: null,
  ENGAGEMENT: null
}); // These types all act the same way and are the only ones returned by the supported endpoint

export const SUPPORTED_AUDIENCE_TYPES = [AudienceTypes.CONTACT_LIST, AudienceTypes.WEBSITE, AudienceTypes.LOOKALIKE, AudienceTypes.COMPANY_LIST];
export const AudienceSources = KeyMirror({
  HUBSPOT_CONTACT_LIST: null,
  HUBSPOT_CONTACT_SEARCH: null,
  SEED_AUDIENCE: null,
  NETWORK_PIXEL: null,
  HUBSPOT_COMPANY_LIST: null,
  ENGAGEMENT: null
});
export const CompanyListTypes = KeyMirror({
  TARGET_COMPANIES: null,
  TIER1_COMPANIES: null,
  TIER2_COMPANIES: null,
  TIER3_COMPANIES: null,
  COMPANY_LIST: null
});
export const VisitorRuleTypes = KeyMirror({
  ALL_WEBSITE_VISITORS: null,
  LANDING_PAGES: null,
  BLOG_PAGES: null,
  WEBSITE_PAGES: null,
  ALL_PAGES: null,
  URL_CONTAINS: null,
  URL_EQUALS: null
});
export const LockedVisitorRuleTypes = KeyMirror({
  ALL_WEBSITE_VISITORS: null,
  LANDING_PAGES: null,
  URL_CONTAINS: null
});
export const RETENTION_DAY_OPTIONS = [7, 14, 30, 60, 90, 180];
export const LINKEDIN_RETENTION_DAYS = 90;
export const AudienceSourcesToAudienceType = {
  [AudienceSources.HUBSPOT_CONTACT_LIST]: AudienceTypes.CONTACT_LIST,
  [AudienceSources.HUBSPOT_CONTACT_SEARCH]: AudienceTypes.CONTACT_LIST,
  [AudienceSources.SEED_AUDIENCE]: AudienceTypes.LOOKALIKE,
  [AudienceSources.NETWORK_PIXEL]: AudienceTypes.WEBSITE,
  [AudienceSources.HUBSPOT_COMPANY_LIST]: AudienceTypes.COMPANY_LIST,
  [AudienceSources.ENGAGEMENT]: AudienceTypes.ENGAGEMENT
};
export const AdwordsUserListTargetingSetting = KeyMirror({
  BID_ONLY: null,
  TARGET_AND_BID: null
});
export const AudienceStates = KeyMirror({
  CREATED: null,
  PROCESSING: null,
  INITIAL_SYNC_COMPLETED: null,
  ERROR: null,
  PIXEL_REMOVED: null,
  PIXEL_MISSING: null
});
export const FBAdEffectiveStatuses = KeyMirror({
  ACTIVE: null,
  PAUSED: null,
  DELETED: null,
  PENDING_REVIEW: null,
  DISAPPROVED: null,
  PREAPPROVED: null,
  PENDING_BILLING_INFO: null,
  CAMPAIGN_PAUSED: null,
  ARCHIVED: null,
  ADSET_PAUSED: null
});
export const FBAdEffectiveStatusToUIStatusTagMap = {
  [FBAdEffectiveStatuses.ACTIVE]: {
    use: 'success',
    hollow: false
  },
  [FBAdEffectiveStatuses.PAUSED]: {
    use: 'danger',
    hollow: false
  },
  [FBAdEffectiveStatuses.DELETED]: {
    use: 'danger',
    hollow: true
  },
  [FBAdEffectiveStatuses.PENDING_REVIEW]: {
    use: 'warning',
    hollow: false
  },
  [FBAdEffectiveStatuses.DISAPPROVED]: {
    use: 'danger',
    hollow: true
  },
  [FBAdEffectiveStatuses.PREAPPROVED]: {
    use: 'active',
    hollow: true
  },
  [FBAdEffectiveStatuses.PENDING_BILLING_INFO]: {
    use: 'danger',
    hollow: true
  },
  [FBAdEffectiveStatuses.CAMPAIGN_PAUSED]: {
    use: 'danger',
    hollow: false
  },
  [FBAdEffectiveStatuses.ARCHIVED]: {
    use: 'paused',
    hollow: true
  },
  [FBAdEffectiveStatuses.ADSET_PAUSED]: {
    use: 'paused',
    hollow: false
  }
};
export const EventTypes = KeyMirror({
  CRM_EVENT: null
});
export const OfflineEventTriggers = KeyMirror({
  LIFECYCLE_STAGE_CHANGE: null,
  PAGE_VIEWS: null,
  FORM_SUBMISSION: null
});
export const TrackingStatusCodes = KeyMirror({
  TRACKED: null,
  UNTRACKED: null,
  NOT_APPLICABLE: null
});
export const TrackingStatusKeys = KeyMirror({
  ALERT: null,
  TRACKING_ENABLED: null,
  TRACKING_OFF: null,
  TRACKING_PENDING: null,
  TRACKING_LIMITED: null,
  LEAD_GEN: null
});
export const AdCreationErrors = KeyMirror({
  MISSING_AD_ACCOUNT: null,
  PERMISSIONS_NEEDED: null,
  MISSING_CAMPAIGN: null,
  MISSING_CAMPAIGN_NAME: null,
  MISSING_PAGE: null,
  MISSING_IMAGE: null,
  MISSING_HEADLINE: null,
  MISSING_HEADLINE_1: null,
  MISSING_HEADLINE_2: null,
  MISSING_HEADLINES: null,
  MISSING_TEXT: null,
  MISSING_DESCRIPTION: null,
  MISSING_DESCRIPTIONS: null,
  MISSING_FORM: null,
  MISSING_LOCATION: null,
  MISSING_BUDGET: null,
  MISSING_SCHEDULE: null,
  MISSING_WEBSITE_URL: null,
  MISSING_SELECTED_POST: null,
  MISSING_SELECTED_LIST: null,
  MISSING_AUDIENCE: null,
  MISSING_KEYWORDS: null,
  MISSING_LANGUAGE: null,
  INVALID_KEYWORDS: null,
  MISSING_THUMBNAIL: null,
  INVALID_WEBSITE_URL: null,
  SCHEDULE_TOO_SHORT: null,
  SCHEDULE_NEEDS_END_DATE: null,
  TARGETING_TOO_SMALL: null,
  BUDGET_TOO_LOW: null,
  BUDGET_TOO_LOW_NON_USD: null,
  VIDEO_STILL_UPLOADING: null,
  VIDEO_UPLOAD_FAILED: null,
  TOS_NOT_ACCEPTED: null,
  NO_UNSAVED_CHANGES: null,
  MISSING_WORKFLOW_NAME: null,
  IS_EXPANDED_TEXT_AD: null,
  MISSING_BENEFICIARY: null
});
export const AdCreationWarnings = KeyMirror({
  PAGE_NOT_SAME_BUSINESS_AS_AD_ACCOUNT: null
}); // Taken from FileManagerCore.

export const FileTypes = KeyMirror({
  IMG: null,
  MOVIE: null,
  DOCUMENT: null,
  AUDIO: null,
  OTHER: null
}); // Equivalent to backend com.hubspot.ads.data.creation.UploadedFileStatus
// except that doesn't have PRE_UPLOAD, which is a custom status on our end
// to prevent fetching the upload status before we make the upload call.

export const MediaUploadStatus = KeyMirror({
  PRE_UPLOAD: null,
  IN_PROGRESS: null,
  DONE: null,
  FAILED: null
});
export const VideoErrors = KeyMirror({
  DIMENSIONS: null,
  ASPECT_RATIO: null
});
export const AUDIENCE_SIZE_CUTOFF = {
  [Networks.LINKEDIN]: 300,
  [Networks.FACEBOOK]: 1000,
  [Networks.ADWORDS]: 0
};
export const MINIMUM_ESTIMATE_BY_NETWORK = {
  [Networks.LINKEDIN]: 300,
  [Networks.FACEBOOK]: 0 // Facebook will send -1 if the audience is too small to be used.

};
export const MINIMUM_TARGETABLE_AGE = 18;
export const MAXIMUM_TARGETABLE_AGE = 65;
export const MIN_DAILY_BUDGET_IN_USD = {
  [Networks.FACEBOOK]: 1,
  [Networks.LINKEDIN]: 10
};
export const BudgetTypes = KeyMirror({
  DAILY: null,
  TOTAL: null,
  CUSTOM: null,
  UNKNOWN: null
}); // Any values here must match those expected by the Facebook SDK.

export const FBLeadAdCallsToAction = KeyMirror({
  VALUE_APPLY_NOW: null,
  VALUE_BOOK_TRAVEL: null,
  VALUE_DOWNLOAD: null,
  VALUE_GET_OFFER: null,
  VALUE_GET_QUOTE: null,
  VALUE_LEARN_MORE: null,
  VALUE_SIGN_UP: null,
  VALUE_SUBSCRIBE: null
});
export const LinkedInLeadAdCallsToAction = KeyMirror({
  APPLY_NOW: null,
  DOWNLOAD: null,
  GET_QUOTE: null,
  LEARN_MORE: null,
  SIGN_UP: null,
  SUBSCRIBE: null,
  REGISTER: null
});
/** Lead ad "Call to action" options when the user is ungated from the LinkedIn Versioned API */

export const LinkedInVersionedAPILeadAdCallsToAction = KeyMirror({
  APPLY: null,
  DOWNLOAD: null,
  VIEW_QUOTE: null,
  LEARN_MORE: null,
  SIGN_UP: null,
  SUBSCRIBE: null,
  REGISTER: null
});
export const GATES = {
  // These are users that were in the beta but would no longer have the new scope and are grandfathered in during ungating to all
  ADVANCED_ADWORDS_SETTINGS: 'Ads:AdvancedAdWordsSettings',
  ENHANCED_MANAGEMENT: 'Ads:EnhancedManagement',
  UNLIMITED_AD_ACCOUNTS: 'Ads:UnlimitedAdAccounts',
  SMOKE_ALARM: 'Ads:LinkedInSmokeAlarmFeb23',
  FACEBOOK_LOCAL_CACHE: 'Ads:FacebookLocalCacheOnly',
  FACEBOOK_CAPI_MIGRATION: 'Ads:FacebookCAPIMigration',
  GOOGLE_ENHANCED_CONVERSIONS: 'Ads:GoogleEnhancedConversions',
  AUTOMATION_MULTIPLE_WORKFLOWS: 'Ads:AutomationMultipleWorkflows',
  GOALS_OOTB_TEMPLATES: 'Goals:OOTBTemplates',
  LISTS_CLIENT_MIGRATION: 'Ads:InboundDbListsClientMigration',
  FACEBOOK_CAPI_LCS_EVENTS: 'Ads:FacebookCapiLifecycleStageEvents',
  FACEBOOK_CAPI_FORM_EVENT_UPDATES: 'Ads:FacebookCapiFormEventsUpdates',
  GOOGLE_ENHANCED_CONVERSIONS_LCS_EVENTS: 'Ads:GoogleECforLifecycleStageEvents',
  ADS_CONTENT_ASSISTANT_PHASE_1: 'Ads:ContentAssistantPhase1'
};
export const AdCreationTypes = {
  LEAD_AD: 'lead-ad',
  BOOSTED_LEAD_AD: 'boosted-lead-ad',
  TRAFFIC: 'website-traffic',
  SEQUENCE: 'sequence',
  SEARCH: 'search',
  LEGACY_SEARCH: 'add-on-search',
  POST_ENGAGEMENT: 'post-engagement'
};
export const FBLeadFormFields = KeyMirror({
  first_name: null,
  last_name: null,
  email: null,
  work_email: null,
  company_name: null,
  phone_number: null,
  street_address: null,
  city: null,
  state: null,
  country: null,
  province: null,
  post_code: null,
  zip_code: null,
  job_title: null,
  work_phone_number: null,
  date_of_birth: null,
  gender: null,
  marital_status: null,
  relationship_status: null,
  military_status: null,
  full_name: null
});
export const LILeadFormFields = KeyMirror({
  FIRST_NAME: null,
  LAST_NAME: null,
  PHONE_NUMBER: null,
  EMAIL: null,
  CITY: null,
  STATE: null,
  COUNTRY: null,
  ZIP_CODE: null,
  JOB_TITLE: null,
  JOB_FUNCTION: null,
  SENIORITY: null,
  COMPANY_NAME: null,
  COMPANY_SIZE: null,
  INDUSTRY: null,
  DEGREE: null,
  FIELD_OF_STUDY: null,
  SCHOOL: null,
  START_DATE: null,
  GRADUATION_DATE: null,
  WORK_PHONE_NUMBER: null,
  WORK_EMAIL: null,
  LINKEDIN_PROFILE_LINK: null
});
export const LeadFormErrors = KeyMirror({
  FORM_ERRORS: null,
  THANK_YOU_ERRORS: null,
  NO_NAME: null,
  INTRO_ERRORS: null
});
export const LEAD_FORM_THANK_YOU_FIELD_LIMIT = 60;
export const LEAD_FORM_THANK_YOU_BODY_FIELD_LIMIT = 300;
export const LINKEDIN_FIELD_LIMITS = {
  offerHeadline: 60,
  description: 160
};
export const FACEBOOK_NOTICE_AND_CONSENT_HEADLINE_LIMIT = 60;
export const FACEBOOK_CONTEXT_CARD_HEADLINE_LIMIT = 60;
export const LinkedInThankYouCTATypes = KeyMirror({
  LEARN_MORE: null,
  VIEW_NOW: null,
  DOWNLOAD_NOW: null,
  TRY_NOW: null,
  VISIT_COMPANY_WEBSITE: null
});
export const NoticeAndConsentTypes = KeyMirror({
  NONE: null,
  LINKEDIN_NONE: null,
  CONSENT_TEXT: null,
  CONSENT_CHECKBOXES: null,
  LEGITIMATE_INTEREST: null,
  CUSTOM: null
});
export const LeadAdPreviewSteps = KeyMirror({
  AD: null,
  FORM: null,
  NOTICE_AND_CONSENT: null,
  THANK_YOU: null
});
export const HUB_ATTRIBUTES = {
  LEGITIMATE_INTEREST_TEXT: 'legitimateInterestPrivacyPolicyText',
  IMPLICIT_CONSENT_TEXT: 'implicitProcessingConsentText',
  EXPLICIT_CONSENT_TEXT: 'explicitProcessingConsentText',
  EXPLICIT_CONSENT_CHECKBOX: 'explicitProcessingConsentCheckboxLabel',
  CONSENT_TEXT: 'communicationConsentText',
  CONSENT_CHECKBOX_DATA: 'communicationConsentCheckboxes'
};
export const FBGeolocationTypes = KeyMirror({
  COUNTRY_GROUP: null,
  COUNTRY: null,
  CITY: null,
  REGION: null,
  GEO_MARKET: null,
  CUSTOM_LOCATION: null
}); // Unfortunately, QA app doesn't have business_management scope

export const isQA = window.location.hostname.indexOf('hubspotqa') > -1;
export const LegacyFacebookScopes = KeyMirror(Object.assign({
  ads_management: null,
  leads_retrieval: null,
  manage_pages: null,
  pages_show_list: null
}, isQA ? {} : {
  business_management: null
}));
export const FacebookScopes = KeyMirror(Object.assign({
  ads_management: null,
  leads_retrieval: null,
  pages_manage_ads: null,
  pages_manage_metadata: null,
  pages_read_engagement: null,
  pages_read_user_content: null,
  pages_show_list: null
}, isQA ? {} : {
  business_management: null
}));
export const PERMISSIONS_ERROR_STRINGS = ['OperationAccessDenied.SET_OPERATION_NOT_PERMITTED', 'NO_PERMISSION_ON_ENTITY', 'ACCESS_PERMISSION', 'Permissions error', 'No Write Permission to This Ad Account', 'OAuthException: (#200) Permissions error', 'USER_PERMISSION_DENIED', 'PERMISSION_DENIED'];
export const MissingPermissionTypes = KeyMirror({
  LEAD_SYNCING: null
});
export const MissingPermissionTypesForNetwork = {
  [MissingPermissionTypes.LEAD_SYNCING]: {
    [Networks.FACEBOOK]: [FacebookScopes.leads_retrieval, LegacyFacebookScopes.manage_pages, FacebookScopes.pages_show_list, FacebookScopes.pages_manage_ads, FacebookScopes.pages_manage_metadata, FacebookScopes.pages_read_engagement, FacebookScopes.pages_read_user_content, ...(isQA ? [] : [FacebookScopes.business_management])]
  }
};
export const AUDIENCE_MATCH_SIZE_TOO_SMALL_ERROR = 'Audience is too small to be used in campaign creation.';
export const AudienceCreationPanels = KeyMirror({
  AUDIENCE_PANEL_NAVIGATOR: null,
  AUDIENCE_TYPE_SELECTION: null,
  WEBSITE: null,
  CONTACT_LIST: null,
  LOOKALIKE: null,
  COMPANY_LIST: null
});
export const SyncingStatus = KeyMirror({
  NOT_READY: null,
  ARCHIVED: null,
  SYNCING_IN_PROGRESS: null,
  MATCHING: null,
  READY: null,
  SYNCING_ERROR: null
});
export const SyncingStatusToTag = {
  [SyncingStatus.NOT_READY]: {
    use: 'active',
    hollow: false
  },
  [SyncingStatus.ARCHIVED]: {
    use: 'danger',
    hollow: false
  },
  [SyncingStatus.SYNCING_IN_PROGRESS]: {
    color: CHART_PURPLE,
    hollow: false
  },
  [SyncingStatus.MATCHING]: {
    color: CHART_PURPLE,
    hollow: true
  },
  [SyncingStatus.READY]: {
    use: 'active',
    hollow: false
  },
  [SyncingStatus.SYNCING_ERROR]: {
    use: 'paused',
    hollow: false
  }
};
export const WELCOME_STATE_EXPERIMENT_TYPES = {
  NORMAL: 'control',
  SIMPLIFIED: 'variant'
};
export const KBArticles = KeyMirror({
  CONNECT_FACEBOOK_ADS_ACCOUNT: null,
  CONNECT_GOOGLE_ADS_ACCOUNT: null,
  CONNECT_LINKEDIN_ADS_ACCOUNT: null,
  AD_TRACKING: null,
  CREATE_FACEBOOK_ADS: null,
  CONFIGURE_ROI_CALCULATION: null,
  ADS_ATTRIBUTION: null,
  GDPR_FUNCTIONALITY: null,
  FACEBOOK_LEAD_ADS_FAQ: null,
  CREATE_AUDIENCES: null,
  FACEBOOK_LEAD_SYNCING: null,
  LINKEDIN_LEAD_SYNCING: null,
  USE_FACEBOOK_AUDIENCES: null,
  USE_LINKEDIN_AUDIENCES: null,
  USE_ADWORDS_AUDIENCES: null,
  HUBSPOT_ADS_PRIVACY: null,
  DATA_DOESNT_MATCH: null,
  QUARANTINED_CONTACTS: null,
  CREATE_EVENTS: null,
  CREATE_COMPANY_LISTS: null,
  FACEBOOK_ADS_TROUBLESHOOT: null,
  ADS_GET_STARTED: null,
  FIX_AD_TRACKING: null,
  MARKETING_CONTACTS: null,
  SMART_RULES: null,
  FACEBOOK_CAPI_BETA: null,
  LIFECYCLE_STAGES: null,
  ALLOW_MULTIPLE_USERS: null,
  CUSTOM_PROPERTIES: null,
  INSTALL_PIXELS_EXTERNAL_NETWORK: null,
  SYNC_GOOGLE_FORM_SUBMISSION_EVENTS: null,
  SYNC_FB_FORM_SUBMISSION_EVENTS: null,
  CREATE_AD_GOALS: null,
  SYNC_FB_CONVERSION_EVENTS: null
});
export const KBArticlesToURLs = {
  [KBArticles.CONNECT_FACEBOOK_ADS_ACCOUNT]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/connect-your-facebook-ads-account-to-hubspot',
  [KBArticles.CONNECT_GOOGLE_ADS_ACCOUNT]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/connect-your-google-ads-account-to-hubspot',
  [KBArticles.CONNECT_LINKEDIN_ADS_ACCOUNT]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/connect-your-linkedin-ads-account-to-hubspot',
  [KBArticles.AD_TRACKING]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/ad-tracking-in-hubspot',
  [KBArticles.CREATE_FACEBOOK_ADS]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/create-facebook-lead-ads-in-hubspot',
  [KBArticles.CONFIGURE_ROI_CALCULATION]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/configure-the-calculation-of-roi-for-paid-ad-campaigns',
  [KBArticles.ADS_ATTRIBUTION]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/ads-attribution-in-hubspot',
  [KBArticles.GDPR_FUNCTIONALITY]: 'https://knowledge.hubspot.com/articles/kcs_article/account/how-do-i-turn-on-gdpr-functionality-in-my-hubspot-account',
  [KBArticles.FACEBOOK_LEAD_ADS_FAQ]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/facebook-lead-ads-faq',
  [KBArticles.CREATE_AUDIENCES]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/create-ads-audiences-in-hubspot',
  [KBArticles.FACEBOOK_LEAD_SYNCING]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/manage-facebook-pages-syncing-leads-to-hubspot',
  [KBArticles.LINKEDIN_LEAD_SYNCING]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/manage-linkedin-ads-accounts-syncing-leads-to-hubspot',
  [KBArticles.USE_FACEBOOK_AUDIENCES]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/target-facebook-ads-using-hubspot-audiences',
  [KBArticles.USE_LINKEDIN_AUDIENCES]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/target-linkedin-ads-using-hubspot-audiences',
  [KBArticles.USE_ADWORDS_AUDIENCES]: 'https://knowledge.hubspot.com/articles/kcs_article/ads/associate-google-ads-audiences-with-a-campaign',
  [KBArticles.HUBSPOT_ADS_PRIVACY]: 'https://knowledge.hubspot.com/ads/hubspot-ads-privacy-features',
  [KBArticles.DATA_DOESNT_MATCH]: 'https://knowledge.hubspot.com/reports/data-in-hubspot-ads-doesnt-match-external-ad-networks',
  [KBArticles.QUARANTINED_CONTACTS]: 'https://knowledge.hubspot.com/email/what-does-it-mean-if-my-lists-have-been-quarantined',
  [KBArticles.CREATE_EVENTS]: 'https://knowledge.hubspot.com/ads/create-and-sync-crm-lifecycle-events-with-your-google-ads-or-facebook-ad-accounts',
  [KBArticles.CREATE_COMPANY_LISTS]: 'https://knowledge.hubspot.com/ads/create-ads-audiences-in-hubspot',
  [KBArticles.FACEBOOK_ADS_TROUBLESHOOT]: 'https://knowledge.hubspot.com/ads/facebook-ads-account-troubleshooting',
  [KBArticles.ADS_GET_STARTED]: 'https://knowledge.hubspot.com/ads/get-started-with-ads#tracking-and-lead-syncing',
  [KBArticles.FIX_AD_TRACKING]: 'https://knowledge.hubspot.com/ads/troubleshoot-ad-tracking-issues',
  [KBArticles.MARKETING_CONTACTS]: 'https://knowledge.hubspot.com/contacts/marketing-contacts',
  [KBArticles.SMART_RULES]: 'https://knowledge.hubspot.com/cos-general/add-smart-content-to-your-website-pages-landing-pages-and-emails',
  [KBArticles.FACEBOOK_CAPI_BETA]: 'https://knowledge.hubspot.com/ads/share-event-and-website-data-with-facebook-via-the-conversions-api',
  [KBArticles.LIFECYCLE_STAGES]: 'https://knowledge.hubspot.com/contacts/use-lifecycle-stages',
  [KBArticles.ALLOW_MULTIPLE_USERS]: 'https://knowledge.hubspot.com/ads/allow-multiple-users-to-manage-your-facebook-linkedin-and-google-ads-accounts',
  [KBArticles.CUSTOM_PROPERTIES]: 'https://knowledge.hubspot.com/crm-setup/manage-your-properties',
  [KBArticles.INSTALL_PIXELS_EXTERNAL_NETWORK]: 'https://knowledge.hubspot.com/ads/install-pixels-from-external-ad-networks',
  [KBArticles.SYNC_GOOGLE_FORM_SUBMISSION_EVENTS]: 'https://knowledge.hubspot.com/ads/create-and-sync-form-ad-conversion-events-with-your-google-ads-account',
  [KBArticles.SYNC_FB_FORM_SUBMISSION_EVENTS]: 'https://knowledge.hubspot.com/ads/create-and-sync-form-ad-conversion-events-with-your-facebook-ads-account',
  [KBArticles.CREATE_AD_GOALS]: 'https://knowledge.hubspot.com/forecast/create-ad-goals',
  [KBArticles.SYNC_FB_CONVERSION_EVENTS]: 'https://knowledge.hubspot.com/ads/create-and-sync-ad-conversion-events-with-your-facebook-ads-account'
}; // If we link to a specific section of a KB article, include and reference it here.

export const KBArticleSections = {
  [KBArticles.AD_TRACKING]: {
    TROUBLESHOOT: 'troubleshoot-ad-tracking-issues',
    HOW_IT_WORKS: 'how-ad-tracking-in-hubspot-works'
  },
  [KBArticles.CREATE_AUDIENCES]: {
    CONTACT_LIST: 'contact-list',
    COMPANY_LIST: 'create-a-linkedin-company-list-audience-marketing-hub-starter-professional-and-enterprise-only',
    LOOKALIKE: 'create-a-lookalike-audience',
    AUDIENCE_SIZE_REQUIREMENT: 'audience-size-requirements'
  },
  [KBArticles.CREATE_FACEBOOK_ADS]: {
    SETUP: 'setup'
  },
  [KBArticles.FACEBOOK_LEAD_ADS_FAQ]: {
    PRIVACY: 'privacy'
  },
  [KBArticles.HUBSPOT_ADS_PRIVACY]: {
    CONTACT_LIST_AUDIENCES: 'sync-your-contact-list-audiences-with-google-facebook-and-linkedin-marketing-hub-starter-professional-and-enterprise-only'
  },
  [KBArticles.FACEBOOK_ADS_TROUBLESHOOT]: {
    USER_PERMISSIONS: '2-check-your-user-permissions'
  },
  [KBArticles.ADS_GET_STARTED]: {
    TRACKING_AND_LEAD_SYNCING: 'tracking-and-lead-syncing'
  },
  [KBArticles.FIX_AD_TRACKING]: {
    REDIRECT_AD_URLS: 'redirected-ad-urls',
    OVERRIDING_CUSTOMIZED_TRACKING: 'overriding-customized-tracking',
    INSUFFICIENT_PERMISSIONS: 'insufficient-permissions',
    LI_SHARED_CONTENT: 'your-ad-is-using-shared-content-or-the-tracking-has-been-edited',
    FB_TOO_MANY_ENGAGEMENTS: 'too-many-engagements',
    FB_INVALID_IMAGE_CROP: 'invalid-crop-size'
  },
  [KBArticles.CUSTOM_PROPERTIES]: {
    CREATE_CUSTOM_PROPERTIES: 'create-custom-properties'
  }
};
export const COUPON_LEARN_MORE = 'http://hubspot.com/behind-the-screens/network-credit-details';
export const PRIOR_SEQUENCING_CONTACT_LIST_COUNT = 3;
export const AFTER_SEQUENCING_CONTACT_LIST_COUNT = 2;
export const USERS_AND_TEAMS_TYPE = {
  TEAM: 'TEAM',
  USER: 'USER',
  CUSTOM: 'CUSTOM'
};
export const CUSTOM_IDS = {
  ASSIGNED_TO_USER: 'ASSIGNED_TO_USER',
  ASSIGNED_TO_USERS_TEAM: 'ASSIGNED_TO_USERS_TEAM',
  ALL: 'ALL'
};
export const GOOGLE_SUPPORT_BILLING_LINK = 'https://support.google.com/google-ads/answer/2497582?hl=en';
export const FACEBOOK_BUSINESS_PAYMENT_LINK = 'https://www.facebook.com/business/help/132073386867900?helpref=page_content';
export const EVENT_CREATION_ERROR_TYPES = {
  APP_SCOPE: 'APP_SCOPE',
  TERMS_OF_SERVICE_NOT_ACCEPTED: 'TERMS_OF_SERVICE_NOT_ACCEPTED'
};
export const EVENT_CREATION_RESPONSE_MESSAGE_KEYWORDS = {
  MODIFY_OFFLINE_CONVERSION_DATA_SETS: 'MODIFY_OFFLINE_CONVERSION_DATA_SETS'
};
export const LINKEDIN_CAMPAIGN_MANAGER_URL = 'https://www.linkedin.com/campaignmanager/accounts'; // https://product.hubteam.com/docs/appsystems/hubs/index.html#hub-types

export const HUB_TYPES = {
  DEVELOPER_TEST: 8
};